import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../styles/Portfolio.css';
import Footer from '../components/Footer';

const sections = [
  {
    id: 'shoot-bts',
    title: 'Shoot BTS',
    images: [
      '/assets/img/portfolio/DSC00001_11zon.jpg',
      '/assets/img/portfolio/DSC00002_11zon.webp',
      '/assets/img/portfolio/DSC00003_11zon.jpg',
      '/assets/img/portfolio/DSC00006_11zon.png',
      '/assets/img/portfolio/DSC00007_11zon.jpg',
      '/assets/img/portfolio/DSC08330.jpg',      '/assets/img/portfolio/1A_11zon.jpg',
      '/assets/img/portfolio/2A_11zon.jpg',
      '/assets/img/portfolio/4_11zon (1).jpg',
      '/assets/img/portfolio/4_11zon (2).jpg',
      '/assets/img/portfolio/4_11zon.jpg',
      '/assets/img/portfolio/5_11zon.jpg',
      '/assets/img/portfolio/6_11zon.jpg',
      '/assets/img/portfolio/8_11zon.jpg'
      
    ]
  },
  {
    id: 'fashion-shoots',
    title: 'Fashion Shooting',
    images: [
      '/assets/img/portfolio/1A_11zon.jpg',
      '/assets/img/portfolio/2A_11zon.jpg',
      '/assets/img/portfolio/4_11zon (1).jpg',
      '/assets/img/portfolio/4_11zon (2).jpg',
      '/assets/img/portfolio/4_11zon.jpg',
      '/assets/img/portfolio/5_11zon.jpg',
      '/assets/img/portfolio/6_11zon.jpg',
      '/assets/img/portfolio/8_11zon.jpg'
    ]
  },
  {
    id: 'brand-stories',
    title: 'Brand Stories',
    images: [
      '/assets/img/portfolio/326A0189_optimized_4000.jpg',
      '/assets/img/portfolio/DSC00003_11zon.jpg',
      '/assets/img/portfolio/DSC08330.jpg'
    ]
  },
  {
    id: 'events',
    title: 'Events & Campaigns',
    images: [
      '/assets/img/portfolio/2A_11zon.jpg',
      '/assets/img/portfolio/5_11zon.jpg',
      '/assets/img/portfolio/8_11zon.jpg'
    ]
  }
];

const GallerySection = ({ title, images, onOpenLightbox }) => {
  const [showAll, setShowAll] = useState(false);
  const sectionRef = useRef(null);
  const visibleImages = useMemo(() => (showAll ? images : images.slice(0, 10)), [showAll, images]);

  // Ensure newly revealed cards are visible immediately (avoid staying at opacity:0)
  useEffect(() => {
    const grid = sectionRef.current;
    if (!grid) return;
    const cards = grid.querySelectorAll('.gallery-card');
    // Next tick to allow DOM to paint new nodes
    const id = setTimeout(() => {
      cards.forEach((c) => c.classList.add('in-view'));
    }, 0);
    return () => clearTimeout(id);
  }, [showAll, images.length]);

  return (
    <div className="gallery-section" data-aos="fade-up">
      <div className="gallery-header">
        <h3 className="gallery-title">{title}</h3>
        <button
          className={`view-more-btn ${images.length <= 10 ? 'is-disabled' : ''}`}
          onClick={() => setShowAll(v => !v)}
          disabled={images.length <= 10}
        >
          {showAll ? 'View less' : 'View more'}
        </button>
      </div>

      <div className="gallery-grid collage-grid" ref={sectionRef}>
        {visibleImages.map((src, idx) => {
          const pattern = ['wide','square','tall','square','wide','square','superwide','tall','square','wide'];
          const size = pattern[idx % pattern.length];
          return (
          <div className={`gallery-card collage-item gallery-card--${size}`} key={`${src}-${idx}`}>
            <button className="gallery-card-btn" onClick={() => onOpenLightbox(images, images.indexOf(src))} aria-label="Open image">
              <img src={src} alt={`${title} ${idx + 1}`} loading="lazy" />
            </button>
            <div className="gallery-overlay">
              <div className="gallery-overlay-inner">
                <div className="overlay-title">{title}</div>
                <button className="overlay-cta" type="button" aria-label="View image" onClick={() => onOpenLightbox(images, images.indexOf(src))}>View</button>
              </div>
            </div>
          </div>
        );})}
      </div>
    </div>
  );
};

const Portfolio = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (imgs, startIndex = 0) => {
    setLightboxImages(imgs);
    setLightboxIndex(Math.max(0, Math.min(startIndex, imgs.length - 1)));
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);
  const prevImage = () => setLightboxIndex((i) => (i - 1 + lightboxImages.length) % lightboxImages.length);
  const nextImage = () => setLightboxIndex((i) => (i + 1) % lightboxImages.length);

  useEffect(() => {
    const cards = Array.from(document.querySelectorAll('.gallery-card'));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });

    cards.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Ensure newly revealed images in each section animate in when View more is toggled
  useEffect(() => {
    const containerLists = Array.from(document.querySelectorAll('.gallery-grid'));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
    containerLists.forEach((grid) => {
      const cards = grid.querySelectorAll('.gallery-card:not(.in-view)');
      cards.forEach((c) => io.observe(c));
    });
    return () => io.disconnect();
  });

  useEffect(() => {
    const onKey = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen]);
  return (
    <div className="portfolio-page">
      <section className="portfolio-hero">
        <div className="container">
          <h1>Our Portfolio</h1>
          <p>
            Explore our work presented as simple, elegant galleries. No hover effects — just clean grids showcasing our visuals.
          </p>
        </div>
      </section>

      <section className="portfolio-section">
        <div className="container">
          {sections.map((s) => (
            <GallerySection key={s.id} title={s.title} images={s.images} onOpenLightbox={openLightbox} />
          ))}
        </div>
      </section>

      {lightboxOpen && (
        <div className="lightbox" role="dialog" aria-modal="true">
          <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">×</button>
          <button className="lightbox-nav prev" onClick={prevImage} aria-label="Previous">
            ‹
          </button>
          <img className="lightbox-image" src={lightboxImages[lightboxIndex]} alt={`Image ${lightboxIndex + 1}`} />
          <button className="lightbox-nav next" onClick={nextImage} aria-label="Next">
            ›
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Portfolio;
